/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { UtrvStatus } from '../../constants/status';
import { SurveyModelMinimalUtrv } from '../../model/surveyData';
import { BasicAlert } from '../alert/BasicAlert';
import { checkHasComments } from '../survey/question/questionUtil';
import G17Client from '../../services/G17Client';
import { TextAreaWithAIDraft } from '../../apps/common/components/TextAreaWithAIDraft/TextAreaWithAiDraft';
import './comments.scss';
import { QuestionReducerState } from '../survey/question/questionInterfaces';
import { QUESTION } from '../../constants/terminology';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { useAppSelector } from '../../reducers';
import { ValueHistory } from '../../types/universalTrackerValue';
import { Comment } from './Comment';
import { $createParagraphNode, $createTextNode, $getRoot, EditorState } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';

function canEdit({ isVerifier, isContributor }: { isVerifier: boolean, isContributor: boolean }, status: string) {
  if ([UtrvStatus.Verified, UtrvStatus.Rejected].includes(status as UtrvStatus)) {
    return isVerifier
  }

  return isContributor;
}

interface CommentsInputProps {
  state: Pick<QuestionReducerState, 'table' | 'value' | 'valueData' | 'numberScale' | 'unit'>;
  disabled: boolean;
  comments: string;
  handleComments: (comments: string, editorState?: EditorState) => void;
  hideLabel: boolean;
  isVerifier: boolean;
  isContributor: boolean;
  utrv: SurveyModelMinimalUtrv;
  stakeholderHistory: Pick<ValueHistory, 'action' | 'note' | 'editorState'>;
  verifierHistory: Pick<ValueHistory, 'action' | 'note' | 'editorState'>;
}

export const CommentsInput = (props: CommentsInputProps) => {

  const {
    state,
    disabled,
    comments,
    handleComments,
    hideLabel,
    isVerifier,
    isContributor,
    utrv,
    stakeholderHistory,
    verifierHistory
  } = props;

  const canUseRichTextEditor = useAppSelector(FeaturePermissions.canUseRichTextEditor);

  const hasAction = (isVerifier || isContributor) && !disabled;
  const combinedHistory = [stakeholderHistory, verifierHistory];
  const hasComments = checkHasComments(stakeholderHistory, verifierHistory);

  const getDraftData = () => {
    if (state.table.rows.length > 0) {
      return {
        value: state.value,
        numberScale: state.numberScale,
        unit: state.unit,
        valueData: {
          data: {
            table: state.table.rows
          }
        }
      }
    }
    return {
      value: state.value,
      valueData: state.valueData
    };
  }

  const initiativeId = utrv.initiativeId;

  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(
      () => {
        const root = $getRoot();
        root.clear();
      },
      { onUpdate: () => editor.blur() }
    );
  }, [editor, utrv._id]);

  const handleEditOldNote = (history: Pick<ValueHistory, 'note' | 'editorState'>) => {
    if (history.editorState) {
      const editorState = editor.parseEditorState(history.editorState);
      editor.setEditorState(editorState);
      handleComments(history.note ?? '', editorState);

      return;
    }

    editor.update(
      () => {
        const root = $getRoot();
        root.clear();

        const paragraphNode = $createParagraphNode();
        paragraphNode.append($createTextNode(history.note));
        root.append(paragraphNode);
      },
      { onUpdate: () => editor.blur() }
    );

    handleComments(history.note ?? '', editor.getEditorState());
  };

  const onEditorAIWrite = (output: string) => {
    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const paragraphNode = $createParagraphNode();
      paragraphNode.append($createTextNode(output));
      root.append(paragraphNode);
    });
  };

  return (
    <div className='comment-group'>
      {!hideLabel ? (
        <label className='my-1 mr-2'>
          <i className='fa fa-comment-alt mr-2' />Would you like to submit any further explanation?
        </label>
      ) : null}
      {hasAction ? (
        <TextAreaWithAIDraft
          key={`comments-textarea-ai-${utrv._id}`}
          name='comments'
          className='form-control commentBox'
          maxLength={5000}
          rows={6}
          canUseRichTextEditor={canUseRichTextEditor}
          onEditorAIWrite={onEditorAIWrite}
          onChange={handleComments}
          disabled={disabled}
          value={comments}
          getDraft={() =>
            G17Client.getQuestionFurtherNotesDraft(initiativeId, {
              utrvId: utrv._id,
              draftData: getDraftData(),
            })
          }
        />
      ) : null}
      {combinedHistory.map((h, index) => {
        return h?.note ? (
          <Comment
            key={index ? 'verifier' : 'stakeholder'}
            history={h}
            handleComments={handleEditOldNote}
            canEdit={canEdit(props, h.action)}
            canUseRichTextEditor={canUseRichTextEditor}
          />
        ) : null;
      })}
      {!hasComments && !hasAction ?
        <BasicAlert type='info'>There are no notes associated with this {QUESTION.SINGULAR}</BasicAlert>
        : null}
    </div>
  )
}
